import React from "react";
import { Link } from "react-router-dom";
import { TspCard, TspTable } from "../../../components/primitives";
import { formatDate } from "../../../utils/tspFormat";
import { JOURNEY_STATUS_LABELS, JOURNEY_STATUS_PILL } from "../../../constants/tspJourneyStatus";
import { PAGE_SIZE } from "../useTspJourneysListData";
import { journeyPlacePath } from "../journeyDetailModel";

// In the design the list heading sits on the page background, above the table
// card (not inside it), so it is its own piece: the page places it and the
// table card in separate grid areas (see _journeys.scss).
export function PastJourneysTitle({ title, totals }) {
  return (
    <h2 className="tsp-past-journeys-title">
      {title}
      <span className="tsp-status-pill tsp-status-pill-success">Completed ({totals.completed})</span>
      <span className="tsp-status-pill tsp-status-pill-danger">Cancelled ({totals.cancelled})</span>
    </h2>
  );
}

// One row per JOURNEY PLACE (GET /tsp/journeys/places): each place of a journey
// is its own row, named "<Taluka / Town> - <Temple>" and dated by the place's
// own date (planned on Add Journey, else its recorded visit) — never the
// journey's date range. Status is the journey's (the status filter keeps
// working); Books / Response are that place's alone ("—" without them, or for
// a caller who may not see those records — never a made-up figure). View opens
// the journey with this place open.
export default function PastJourneysTable({ rows, meta, onPageChange }) {
  const columns = [
    {
      key: "name",
      label: "Journey",
      render: (row) => <strong className="tsp-cell-strong">{row.place_label}</strong>,
    },
    // The journey's status, in its own column — Completed included — as the same pill.
    {
      key: "status",
      label: "Status",
      render: (row) =>
        row.status ? (
          <span className={`tsp-status-pill ${JOURNEY_STATUS_PILL[row.status] || "tsp-status-pill-muted"}`}>{JOURNEY_STATUS_LABELS[row.status] || row.status}</span>
        ) : (
          <span className="tsp-cell-muted">—</span>
        ),
    },
    // The place's year (its date's, else its journey's start year — server-derived).
    { key: "year", label: "Year", render: (row) => (row.year ? <strong className="tsp-cell-strong">{row.year}</strong> : <span className="tsp-cell-muted" title="No date yet">No date</span>) },
    { key: "temple", label: "Temple", render: (row) => row.temple_name || "—" },
    { key: "state", label: "State", render: (row) => row.state?.name || "—" },
    { key: "district", label: "District", render: (row) => row.district?.name || "—" },
    {
      key: "date",
      label: "Date",
      render: (row) => (row.date ? formatDate(row.date) : <span className="tsp-cell-muted" title="No date planned or visit recorded for this place yet">—</span>),
    },
    {
      key: "books",
      label: "Books",
      render: (row) => (row.books ? <strong className="tsp-cell-accent">{Number(row.books).toLocaleString("en-IN")}</strong> : <span className="tsp-cell-muted">—</span>),
    },
    {
      key: "response",
      label: "Response",
      render: (row) => (row.response != null ? <strong className="tsp-cell-positive">★ {row.response}</strong> : <span className="tsp-cell-muted">—</span>),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <span className="tsp-table-actions">
          <Link to={journeyPlacePath(row.journey_id, row.id)} title={`View ${row.place_label}`}>
            View
          </Link>
        </span>
      ),
    },
  ];

  const from = meta.total === 0 ? 0 : (meta.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(meta.page * PAGE_SIZE, meta.total);

  return (
    <TspCard className="tsp-past-journeys">
      <TspTable columns={columns} rows={rows} rowKey="id" className="tsp-table-journeys" />
      <div className="tsp-table-pagination">
        <span>
          {from} – {to} of {meta.total}
        </span>
        <span className="tsp-card-action-group">
          <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm" disabled={meta.page <= 1} onClick={() => onPageChange(meta.page - 1)}>
            Previous
          </button>
          <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm" disabled={meta.page >= meta.totalPages} onClick={() => onPageChange(meta.page + 1)}>
            Next
          </button>
        </span>
      </div>
    </TspCard>
  );
}
